/* The Overview panel. Every other panel reports a measure, which is only good or bad against
 * a threshold, so each check here judges one measure against its threshold and the verdict at
 * the top is the worst of them, named rather than only counted.
 *
 * It is also the map of the site: the verdict carries a button into every panel that came back
 * unhappy, and the tiles below link to the panel each figure comes from, so "what needs
 * attention" and "where to go for it" are one click apart rather than two readings of the rail.
 */

import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button, Card } from '@nextleap-al/admin-ui';

import type { GapsResponse, Health, Overview, Tickets } from '../api/types';
import { PanelCard, PanelError } from '../components/Panel';
import { Signal, Signals } from '../components/Signal';
import { StatTiles } from '../components/StatTiles';
import { Table } from '../components/Table';
import { VolumeChart } from '../components/VolumeChart';
import { useApi } from '../hooks/useApi';
import { band, num, plural, shortDate, type CheckStatus } from '../lib/format';
import { tabLabelOf } from '../nav';
import { useApp } from '../state/app';
import { gapReason } from './Gaps';

const MARK: Record<CheckStatus, string> = { good: '✓', warn: '!', bad: '✕', unknown: '?' };

// Never colour alone: the word is what a printout, a colour-blind reader and a screen
// reader all get.
const STATE: Record<CheckStatus, string> = {
  good: 'Fine',
  warn: 'Worth a look',
  bad: 'Needs action',
  unknown: 'Not enough data',
};

const TONE: Record<CheckStatus, string> = {
  good: 'var(--chart-good)',
  warn: 'var(--chart-warning)',
  bad: 'var(--chart-critical)',
  unknown: 'var(--text-muted)',
};

const BADGE: Record<CheckStatus, 'good' | 'warning' | 'critical' | 'plain'> = {
  good: 'good',
  warn: 'warning',
  bad: 'critical',
  unknown: 'plain',
};

/** Worst first, so the verdict names the unhappy checks, and stands their buttons, in the
 *  order they are worth opening. */
const ORDER: Record<CheckStatus, number> = { bad: 0, warn: 1, unknown: 2, good: 3 };

interface Check {
  status: CheckStatus;
  title: string;
  /** The panel this check is read in full on, where one exists for this account. */
  tab?: string;
}

/** The way into the panel a check names. The check itself is read there, so this is the whole
 *  of what the Overview has to say about it. */
function PanelLink({ tab, label }: { tab: string; label: string }) {
  return (
    <Link to={`/${tab}`}>
      <Button
        variant="secondary"
        size="sm"
        rightIcon={<ArrowRight aria-hidden="true" className="h-3.5 w-3.5" />}
      >
        Open {label}
      </Button>
    </Link>
  );
}

export function SummaryPanel() {
  const { days, system, simple } = useApp();
  const overview = useApi<Overview>('/api/overview', { days, system });
  const gaps = useApi<GapsResponse>('/api/gaps', { days, system, limit: 50 });
  const tickets = useApi<Tickets>('/api/tickets', { days, system });
  const health = useApi<Health>('/api/health-metrics', { days, system });

  const error = overview.error ?? gaps.error ?? tickets.error ?? health.error;
  if (error) return <PanelError error={error} />;
  // Four queries, so this is the longest wait on the site. Nothing until all four are in,
  // rather than a sentence or a stand-in: whatever is put here is read as the page and then
  // taken away again, which is what a reload is not supposed to look like.
  if (!overview.data || !gaps.data || !tickets.data || !health.data) {
    return null;
  }

  const c = overview.data.current;
  const open = gaps.data.items.filter((g) => !g.status);
  const total = health.data.failures?.turns || c.turns || 0;
  const failed = health.data.failures?.failed || 0;
  const p50 = health.data.stages.total_ms?.p50 ?? null;

  // The two timing checks are read on Health, which an admin does not have: for that account
  // they are still counted in the verdict, but carry no button to a tab that would refuse them.
  const healthTab = simple ? undefined : 'health';

  const checks: Check[] = [];

  checks.push(
    c.turns
      ? { status: 'good', title: 'People are using it', tab: 'adoption' }
      : { status: 'unknown', title: 'Nobody asked anything' },
  );

  // Under five ratings is an anecdote, not a measurement, and must not be scored as one.
  checks.push({
    status: c.feedback_n < 5 ? 'unknown' : band(1 - (c.helpful_rate ?? 0), 0.3, 0.5),
    title: 'Are the answers good?',
    tab: 'quality',
  });

  // A rate over nothing is not zero: with no traffic the API sends null, which band() reads
  // as "not enough data" rather than as a pass.
  checks.push({
    status: band(c.low_confidence_rate, 0.2, 0.4),
    title: 'Do your documents cover what people ask?',
    tab: 'gaps',
  });

  checks.push({
    status: !total ? 'unknown' : failed === 0 ? 'good' : band(failed / total, 0.01, 0.05),
    title: 'Did anything break?',
    tab: healthTab,
  });

  checks.push({
    status: !tickets.data.filed
      ? 'unknown'
      : tickets.data.delivered >= tickets.data.filed
        ? 'good'
        : 'bad',
    title: 'Do support tickets reach the helpdesk?',
    tab: 'tickets',
  });

  checks.push({
    status: p50 == null ? 'unknown' : band(p50, 6000, 12000),
    title: 'Is it fast enough?',
    tab: healthTab,
  });

  checks.push({
    status: open.length ? 'warn' : 'good',
    title: 'Is anything waiting for you?',
    tab: 'gaps',
  });

  // A period with no traffic is not a healthy period: every check would come back "nothing
  // to report", which must not read as "everything is fine".
  const worst: CheckStatus = !c.turns
    ? 'unknown'
    : checks.some((k) => k.status === 'bad')
      ? 'bad'
      : checks.some((k) => k.status === 'warn')
        ? 'warn'
        : checks.some((k) => k.status === 'good')
          ? 'good'
          : 'unknown';

  const scope = `the last ${num(days)} days${system && system !== 'all' ? ` in ${system}` : ''}`;

  // Everything that is not fine, worst first. A check that passed is its tile above, and
  // nothing else.
  const todo = [...checks]
    .filter((k) => k.status !== 'good')
    .sort((a, b) => ORDER[a.status] - ORDER[b.status]);

  // One button per panel, not per check: two checks can send you to the same place, and
  // "Open Gaps to fix" twice in a row reads as a bug.
  const routes = todo
    .map((k) => k.tab)
    .filter((tab, i, all): tab is string => !!tab && all.indexOf(tab) === i);

  const tally = (['bad', 'warn', 'good', 'unknown'] as CheckStatus[])
    .map((status) => ({ status, n: checks.filter((k) => k.status === status).length }))
    .filter((row) => row.n > 0);

  const headline = {
    good: 'Everything looks fine.',
    warn: 'Mostly fine — a few things are worth a look.',
    bad: 'Something needs attention.',
    unknown: `Nothing was asked in ${scope}.`,
  }[worst];

  // Which checks are unhappy, by name. Without it the verdict says only that something is
  // wrong, and the buttons below are the first hint of what.
  const sub =
    worst === 'unknown'
      ? 'There is nothing to judge yet. Check that the assistant is still switched on inside the ' +
        'system, and that people know it is there.'
      : todo.length
        ? `${plural(checks.length, 'check')} over ${scope}. ` +
          (todo.length === 1
            ? `One of them is not fine: ${todo[0].title}`
            : `${num(todo.length)} of them are not fine, worst first: ` +
              todo.map((k) => k.title).join('; '))
        : `${plural(checks.length, 'check')} over ${scope}, and every one of them passed.`;

  return (
    <div className="space-y-5">
      {/* The verdict first, and above the numbers: what the page is for is the answer to
          "is anything wrong", not the totals it was worked out from. */}
      <Card
        className="border-l-4"
        style={{
          borderLeftColor: TONE[worst],
          background: `color-mix(in srgb, ${TONE[worst]} 5%, var(--bg-elevated))`,
        }}
      >
        <div className="flex flex-wrap items-start gap-4">
          <span
            aria-hidden="true"
            className="flex h-10 w-10 flex-none items-center justify-center rounded-full text-lg font-bold text-[var(--text-inverse)]"
            style={{ background: TONE[worst] }}
          >
            {MARK[worst]}
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-lg font-semibold text-[var(--text-primary)]">{headline}</p>
            <p className="mt-1 max-w-prose text-sm leading-relaxed text-[var(--text-secondary)]">
              {sub}
            </p>
            {/* The way out of the verdict: one button per unhappy panel, worst first. The
                check is measured, explained and acted on there, not here. */}
            {routes.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-2">
                {routes.map((tab) => (
                  <PanelLink key={tab} tab={tab} label={tabLabelOf(tab, simple)} />
                ))}
              </div>
            )}
          </div>
          {/* The checks as a count, so the shape of the page below is readable before it is
              read. The word is on every badge; the colour only repeats it. */}
          <div className="ml-auto">
            <Signals>
              {tally.map((row) => (
                <Signal key={row.status} tone={BADGE[row.status]}>
                  {STATE[row.status]} · {row.n}
                </Signal>
              ))}
            </Signals>
          </div>
        </div>
      </Card>

      {/* The site-wide totals live here and nowhere else: this is the one page whose
          subject is the whole assistant. Each tile is the way into the panel it comes from. */}
      <StatTiles data={overview.data} />

      {/* The one thing a page of totals cannot show: when the questions arrived. It carries
          its own date range, and says so — the tiles above are still the toolbar's period. */}
      <VolumeChart daily={health.data.daily} />

      <PanelCard
        title="The questions to fix first"
        hint="Asked, and answered badly. Writing one document for one of them is the most useful thing you can do."
        action={
          <Link to="/gaps">
            <Button variant="ghost" size="sm">
              See the full list
            </Button>
          </Link>
        }
      >
        <Table
          rows={open.slice(0, 5)}
          empty="Nothing is waiting to be written."
          columns={[
            { key: 'example', label: 'Question' },
            {
              id: 'why',
              label: 'Why it is here',
              render: (g) => gapReason(g, gaps.data!.threshold),
            },
            { key: 'occurrences', label: 'Times asked', num: true },
            {
              id: 'last_seen',
              label: 'Last asked',
              render: (g) => shortDate(g.last_seen),
              sortValue: (g) => g.last_seen,
            },
          ]}
        />
      </PanelCard>
    </div>
  );
}
